import * as React from 'react';
import {Mutation} from 'react-apollo';
// @ts-expect-error
import type {RouterHistory} from 'react-router-dom';
import {Link as RoutedLink, Redirect} from 'react-router-dom';

import {Button} from 'shells/button/button';
import {DisabledSection} from 'shells/disabled-section/disabled-section';
import {FlashBanner} from 'shells/flash-banner/flash-banner';
import {Link} from 'shells/link/link';
import GoogleLogoSvg from 'shells/logos/google-g.svg';
import O365LogoSvg from 'shells/logos/office-365.svg';
import {MessageContainer} from 'shells/message/message-container';
import {RightFrame, ModalMain, NavButtons} from 'shells/modal/step-modal/layout';
import {SpacerGroup} from 'shells/spacer/spacer-group';
import {ModalHeader} from 'shells/typography/modal-header';
import {ModalSubheader} from 'shells/typography/modal-subheader';

import type {
    setGoogleMailSendIsEnabled as setGoogleMailSendIsEnabledMutation,
    setGoogleMailSendIsEnabledVariables as setGoogleMailSendIsEnabledMutationVariables,
    setOffice365MailSendIsEnabled as setOffice365MailSendIsEnabledMutation,
    setOffice365MailSendIsEnabledVariables as setOffice365MailSendIsEnabledMutationVariables,
} from 'nutshell-graphql-types';

import {InitialButton} from '../../integrations/initial-button';

import {QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE} from '../constants';
import {GetEmailSendingSettings} from '../email-sending/get-email-sending-settings';
import {updateCacheFromMutation} from '../email-sending/update-cache-from-mutation';
import SET_GOOGLE_MAIL_SEND_IS_ENABLED from '../graphql/mutations/set-google-mail-send-is-enabled.graphql';
import SET_OFFICE365_MAIL_SEND_IS_ENABLED from '../graphql/mutations/set-office365-mail-send-is-enabled.graphql';

import {trackModalPageLoad} from './track-page-load';

import styles from './choose-provider.module.css';

const o365Logo = <O365LogoSvg width={24} />;
const googleLogo = <GoogleLogoSvg width={24} />;

class GoogleEnableMutation extends Mutation<
    setGoogleMailSendIsEnabledMutation,
    setGoogleMailSendIsEnabledMutationVariables
> {}

class Office365EnableMutation extends Mutation<
    setOffice365MailSendIsEnabledMutation,
    setOffice365MailSendIsEnabledMutationVariables
> {}

type Props = {
    onCancel: () => void;
    prevPage: string;
    history: RouterHistory;
    currentUrl: string;
};

const ALL_EMAIL_TYPES = ['GOOGLE', 'GOOGLE_APPS', 'OFFICE365'];

export function ChooseProvider(props: Props) {
    const [errorMessages, setErrorMessages] = React.useState<string[]>([]);
    // This state is used to track when we're adding a Google or O365 email
    // address, becuase without it, the redirect to SMTP settings happens before
    // the redirect to the connection seccessful page. That would cause the
    // user, after adding a second email integration (not SMTP) to be redirected
    // to the add smtp email page on success.
    const [isAddingIntegration, setIsAddingIntegration] = React.useState<boolean>(false);

    React.useEffect(() => {
        trackModalPageLoad(props.currentUrl);
    }, [props.currentUrl]);

    const banners = (
        <div className={styles.messages}>
            <MessageContainer>
                {errorMessages.map((message) => (
                    <FlashBanner key={message} hasBorder={true} type='error'>
                        {message}
                    </FlashBanner>
                ))}
            </MessageContainer>
        </div>
    );

    return (
        <RightFrame>
            <ModalMain>
                {banners}
                <ModalHeader>Please select your email provider</ModalHeader>
                <ModalSubheader>
                    Once we know who your email provider is, we’ll get connected so that you can
                    send emails from Nutshell.
                </ModalSubheader>
                <GetEmailSendingSettings>
                    {({isLoading, refetch, configuredUserEmails}) => {
                        // Get the email types the user currently has enabled
                        const userEmailTypes = configuredUserEmails
                            .filter((email) => {
                                return email.isEnabledForSending;
                            })
                            .map((email) => {
                                return email.type;
                            });

                        // Get the email types the user can still set up
                        const addableEmailTypes = ALL_EMAIL_TYPES.filter((emailType) => {
                            // @ts-expect-error
                            return !userEmailTypes.includes(emailType);
                        });

                        // If we have no addable email types, are not loading,
                        // and didnt just add a google or o365 email, redirect
                        // to the SMTP email page.
                        if (!addableEmailTypes.length && !isLoading && !isAddingIntegration) {
                            // No email types can be added except SMTP
                            return <Redirect to='/connect-email/configure-smtp' />;
                        }

                        return (
                            <DisabledSection disabled={isLoading} heightFitContent={true}>
                                <div className={styles['account-buttons']}>
                                    {addableEmailTypes.includes('GOOGLE') &&
                                    addableEmailTypes.includes('GOOGLE_APPS') ? (
                                        /* The google integration has 2 possible types (OAuth and Google Apps), so that's why we have to check both are addable */
                                        <GoogleEnableMutation
                                            mutation={SET_GOOGLE_MAIL_SEND_IS_ENABLED}
                                            update={updateCacheFromMutation}
                                            // @ts-expect-error
                                            refetchQueries={QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE}
                                        >
                                            {(enableGoogle) => (
                                                <SpacerGroup>
                                                    <InitialButton
                                                        initialText='Connect with Google'
                                                        authCheckUrl='/google/has-auth'
                                                        windowFeatures={{
                                                            toolbar: 'no',
                                                            location: 'no',
                                                            width: 600,
                                                            height: 850,
                                                        }}
                                                        logo={googleLogo}
                                                        enable={enableGoogle}
                                                        onAuthStart={() => {
                                                            setIsAddingIntegration(true);
                                                            setErrorMessages([]);
                                                        }}
                                                        onAuthSuccess={() => {
                                                            return refetch().then(() => {
                                                                window.dispatchEvent(
                                                                    new Event('email-connected')
                                                                );
                                                                props.history.replace(
                                                                    '/connect-email/connection-successful?p=g'
                                                                );
                                                            });
                                                        }}
                                                        onAuthError={(errMsgs) => {
                                                            setErrorMessages(
                                                                errMsgs.concat(errorMessages)
                                                            );
                                                        }}
                                                    />
                                                </SpacerGroup>
                                            )}
                                        </GoogleEnableMutation>
                                    ) : undefined}
                                    {addableEmailTypes.includes('OFFICE365') ? (
                                        <Office365EnableMutation
                                            mutation={SET_OFFICE365_MAIL_SEND_IS_ENABLED}
                                            update={updateCacheFromMutation}
                                            // @ts-expect-error
                                            refetchQueries={QUERIES_TO_REFETCH_AFTER_EMAIL_UPDATE}
                                        >
                                            {(enableO365) => (
                                                <SpacerGroup>
                                                    <InitialButton
                                                        initialText='Connect with Office 365'
                                                        authCheckUrl='/office365/has-auth'
                                                        windowFeatures={{
                                                            toolbar: 'no',
                                                            location: 'no',
                                                            width: 600,
                                                            height: 500,
                                                        }}
                                                        logo={o365Logo}
                                                        enable={enableO365}
                                                        onAuthStart={() => {
                                                            setIsAddingIntegration(true);
                                                            setErrorMessages([]);
                                                        }}
                                                        onAuthSuccess={() => {
                                                            return refetch().then(() => {
                                                                window.dispatchEvent(
                                                                    new Event('email-connected')
                                                                );
                                                                props.history.replace(
                                                                    '/connect-email/connection-successful?p=o'
                                                                );
                                                            });
                                                        }}
                                                        onAuthError={(errMsgs) => {
                                                            setErrorMessages(
                                                                errMsgs.concat(errorMessages)
                                                            );
                                                        }}
                                                    />
                                                </SpacerGroup>
                                            )}
                                        </Office365EnableMutation>
                                    ) : undefined}
                                </div>
                            </DisabledSection>
                        );
                    }}
                </GetEmailSendingSettings>
                {/* @ts-expect-error */}
                <Link variant='primary' as={RoutedLink} to='/connect-email/configure-smtp'>
                    I use a different provider
                </Link>
            </ModalMain>
            <NavButtons>
                <React.Fragment>
                    {/* @ts-expect-error */}
                    <Link as={RoutedLink} to={props.prevPage}>
                        Back
                    </Link>
                    <Button variant='secondary' size='big' onClick={props.onCancel}>
                        Cancel
                    </Button>
                </React.Fragment>
            </NavButtons>
        </RightFrame>
    );
}
